import { useCallback, useEffect, useState } from 'react';
import { DropDownItem } from 'web-library';
import { getIndexValue } from 'web-library/utils/ArrayUtils';
import { ConfigurationType } from 'web-library/models/DropDown';
import { usePostExportJobStatusReport } from '../../../api-refactored/job-status-report/usePostExportJobStatusReport';
import { FileUploadState } from '../../../models/File';
import { useGetDocumentUploadStatus } from '../../../api-refactored/document/useGetDocumentUploadStatus';

export interface useJobStatusDownloadActionReturn {
  dialogState: boolean;
  fileDownloadStatus: FileUploadState;
  fileUuid: string;
  onHandleExport: () => void;
  onDialogClose: () => void;
}

export const useJobStatusDownloadAction = (
  initialDate: string,
  endDate: string,
  jobUuid: string,
  search: string,
  recordStatus: string[],
  examLevels: DropDownItem[]
): useJobStatusDownloadActionReturn => {
  const [fileDownloadStatus, setFileDownloadStatus] = useState<FileUploadState>(
    FileUploadState.IDLE
  );
  const [dialogState, setDialogState] = useState<boolean>(false);

  const [fileUuid, setFileUuid] = useState('');

  const { mutate: jobStatusDownloadMutate } = usePostExportJobStatusReport({
    onSuccess: (res) => setFileUuid(res.uuid),
  });

  /** to define bulk download
   * */
  const onHandleExport = useCallback(() => {
    const searchPayload = search ?? null;
    let recordStatusPayload: Pick<ConfigurationType, 'code'> | null = null;
    let examLevelsPayload: Pick<ConfigurationType, 'code'>[] | null = null;
    if (recordStatus?.length) {
      recordStatusPayload = { code: getIndexValue(recordStatus, 0) };
    }
    if (examLevels?.length) {
      examLevelsPayload = examLevels.map((item: DropDownItem) => ({
        code: item.value,
      }));
    }

    setDialogState(true);
    jobStatusDownloadMutate({
      jobName: {
        name: '',
        displayName: '',
        description: '',
        uuid: jobUuid,
      },
      startDate: initialDate,
      endDate,
      q: searchPayload,
      recordStatus: recordStatusPayload,
      examLevels: examLevelsPayload,
    });
  }, [jobStatusDownloadMutate, jobUuid, initialDate, endDate, search, recordStatus, examLevels]);

  const {
    data: getReportDocumentData,
    isFetching: isFetchingReportDocumentData,
    refetch: refetchReportDocumentData,
  } = useGetDocumentUploadStatus({
    fileKey: fileUuid || '',
    enabled: !!fileUuid,
    refetchInterval: undefined,
    onSuccess: (res) => {
      setDialogState(true);
      setFileDownloadStatus(res.fileStatus);
      if (res.fileStatus === FileUploadState.FAILED || res.fileStatus === FileUploadState.TIMEOUT) {
        setFileUuid('');
      }
      if (res.fileStatus === FileUploadState.SUCCESS) {
        const newWindow = window.open(res.url, '_blank', 'noopener,noreferrer');
        if (newWindow) {
          newWindow.opener = null;
        }
        setFileUuid('');
        setDialogState(false);
      }
    },
    onError: () => setDialogState(false),
  });

  useEffect(() => {
    if (
      !isFetchingReportDocumentData &&
      getReportDocumentData?.fileStatus === FileUploadState.UPLOADING
    ) {
      refetchReportDocumentData();
    }
  }, [getReportDocumentData?.fileStatus, isFetchingReportDocumentData, refetchReportDocumentData]);

  /** handle dialog close
   * */
  const onDialogClose = () => {
    setDialogState(false);
    setFileUuid('');
  };

  return {
    dialogState,
    fileDownloadStatus,
    fileUuid,
    onHandleExport,
    onDialogClose,
  };
};
